/**
 * Canonical Earning kinds. Mirrors `Earning::KINDS` in
 * `marketpryce-rewards/app/models/earning.rb`. The mobile client and
 * backend MUST agree byte-for-byte; never write a kind string by hand.
 *
 * - `direct`: payout for an action the user themselves completed.
 * - `referral`: payout for an action completed by someone the user
 *   invited (the deal's referrer cut — SoFi Checking today).
 * - `invite_bonus`: app-level invite program — flat bonus to the
 *   referrer when their invitee's FIRST deal pays out (any deal).
 * - `invite_welcome`: the invitee's side of the same moment — extra
 *   on top of their own direct cut.
 */
export const EARNING_KIND = {
  Direct: 'direct',
  Referral: 'referral',
  InviteBonus: 'invite_bonus',
  InviteWelcome: 'invite_welcome',
} as const;
export type EarningKind = (typeof EARNING_KIND)[keyof typeof EARNING_KIND];

/**
 * Canonical Earning lifecycle. Mirrors `Earning::STATUSES`.
 *
 * - `available`: counts toward balance; cashable.
 * - `cashed_out`: paid out via Tremendous; no longer cashable but
 *   still in lifetime totals.
 * - `voided`: brand reversed the conversion; refunded from balance.
 */
export const EARNING_STATUS = {
  Available: 'available',
  CashedOut: 'cashed_out',
  Voided: 'voided',
} as const;
export type EarningStatus = (typeof EARNING_STATUS)[keyof typeof EARNING_STATUS];

/**
 * Nested deal payload returned alongside every earning row. Keeps the
 * earning list rendering self-sufficient — no need to look up Deals
 * from the deals store, which may not be loaded on cold-start of the
 * earnings tab.
 */
export interface EarningDeal {
  slug: string;
  brand_name: string;
  /** Offer name that tells sibling deals apart ("SoFi Invest"); brand_name is the company ("SoFi"). */
  display_name: string;
  brand_category: string;
  brand_logo_url: string | null;
}

export interface EarningPerson {
  id: number;
  public_name: string;
  first_name: string | null;
  initials: string;
}

export interface Earning {
  id: number;
  kind: EarningKind;
  amount_cents: number;
  status: EarningStatus;
  created_at: string;
  person?: EarningPerson | null;
  deal: EarningDeal;
}

/**
 * Page-number pagination envelope returned by the backend earnings
 * index. `has_more` is server-computed from `(page * per_page) < total_count`
 * — trust it rather than re-deriving on the client.
 */
export interface PaginationMeta {
  page: number;
  per_page: number;
  total_count: number;
  has_more: boolean;
}

export interface EarningsListResponse {
  earnings: Earning[];
  meta: PaginationMeta;
}

/**
 * Stats slice of `GET /api/v1/users/me`. The endpoint returns a richer
 * payload (school, sport, click_funnel, etc) but the earnings store
 * only needs the three money totals — anything else is auth.store's
 * responsibility.
 */
export interface MeStats {
  balance_cents: number;
  lifetime_earned_cents: number;
  month_earnings_cents: number;
  school_name: string | null;
}
